import { computed, signal, Signal } from "@angular/core";

import { catchError, Observable, of, tap } from "rxjs";

export interface ExecuteOptions {
  defaultErrorMessage?: string;
  defaultSuccessMessage?: string;

  resetStatusDuration?: number;

  throwErrors?: boolean;
}

/**
 * Enum representing different status type enum for components.
 * @enum {string}
 * 
 * @property IDLE - Indicates the component is not currently processing an operation
 * @property BUSY - Indicates the component is currently processing an operation
 * @property ERROR - Indicates an error has occurred in the component
 * @property WARNING - Indicates a non-critical issue in the component
 * @property SUCCESS - Indicates an operation completed successfully
 */
export enum StatusType {
  IDLE = 'idle',
  BUSY = 'busy',
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success'
};

/**
 * Interface representing a component's status information.
 * 
 * @property type - The current status type enum value
 * @property message - Optional descriptive message associated with the status
 */
export interface ComponentStatus { type: StatusType; message?: string; }

/**
 * Abstract base class providing common functionality for Angular components.
 * 
 * This class implements:
 * - Status management with signals (busy, error, warning, success states)
 * - Theme asset retrieval
 * - Helper methods for status handling
 * 
 * Extend this class in component implementations to inherit these capabilities.
 * 
 * @example
 * ```typescript
 * @Component({
 *   selector: 'app-my-component',
 *   templateUrl: './my-component.component.html'
 * })
 * export class MyComponent extends BaseComponent {
 *   fetchData() {
 *     this.setStatus(StatusType.BUSY, 'Loading data...');
 *     // ... fetch data logic
 *     this.setStatus(StatusType.SUCCESS, 'Data loaded successfully', 3000);
 *   }
 * }
 * ```
 */
export abstract class BaseComponent {

  //#region Status Materials
  // Expose the Status enum to the component
  public readonly StatusType = StatusType;

  // A timeout to reset the status after a specified duration
  private _statusResetTimeout?: any;

  // Initial status state for the component
  private readonly INITIAL_STATUS = { type: StatusType.IDLE } as const;

  /** Signals for component status */
  protected readonly status = signal<ComponentStatus>(this.INITIAL_STATUS)
  protected readonly statusType: Signal<StatusType> = computed(() => this.status().type);
  protected readonly statusMessage: Signal<string | undefined> = computed(() => this.status().message);
  //#endregion

  //#region Execute Methods
  /**
   * Executes an Observable with automatic status handling.
   * 
   * This method provides automatic status management during Observable lifecycle:
   * - Sets status to BUSY on subscription
   * - Sets status to SUCCESS on completion
   * - Sets status to ERROR on error with the error message
   * - Optionally throws or suppresses errors
   * 
   * @param observable The Observable to execute and monitor
   * @param options Configuration options for execution and status handling
   * @returns A wrapped Observable that applies status handling
   * 
   * @example
   * ```typescript
   * // Basic usage
   * this.executeObservable(this.http.get('/api/data'))
   *   .subscribe(data => this.processData(data));
   * 
   * // With options
   * this.executeObservable(
   *   this.userService.updateProfile(profileData),
   *   {
   *     defaultSuccessMessage: 'Profile updated',
   *     defaultErrorMessage: 'Failed to update profile',
   *     resetStatusDuration: 3000
   *   }
   * ).subscribe();
   * ```
   */
  protected executeObservable<T>(observable: Observable<T>, options?: ExecuteOptions): Observable<T | void> {
    return observable.pipe(
      tap({
        // Set component status to busy
        subscribe: () => {
          this.setStatus(StatusType.BUSY);
        },
        // Set component status to error
        error: (error: any) => {
          this.setStatus(StatusType.ERROR, error?.message || options?.defaultErrorMessage, options?.resetStatusDuration);
        },
        // Set component status to success
        complete: () => {
          this.setStatus(StatusType.SUCCESS, options?.defaultSuccessMessage, options?.resetStatusDuration);
        }
      }),
      catchError((error: any) => {
        // If execute option is set to throw errors, rethrow the error.
        if (options?.throwErrors) throw error;

        // Otherwise return undefined result and complete the observable.
        return of(undefined);
      })
    );
  }

  /**
   * Executes an asynchronous Promise-based operation with status handling.
   * 
   * This method provides automatic status management during Promise execution:
   * - Sets status to BUSY when starting
   * - Sets status to SUCCESS on successful completion
   * - Sets status to ERROR on error with the error message
   * - Optionally throws or suppresses errors
   * 
   * @param fn Function that returns a Promise to be executed
   * @param options Configuration options for execution and status handling
   * @returns Promise resolving to the result of the executed function or void if error occurred
   * 
   * @example
   * ```typescript
   * // Basic usage
   * const result = await this.executePromise(() => this.dataService.fetchData());
   * 
   * // With options
   * await this.executePromise(
   *   () => this.authService.login(credentials),
   *   {
   *     defaultSuccessMessage: 'Login successful',
   *     defaultErrorMessage: 'Login failed',
   *     resetStatusDuration: 3000,
   *     throwErrors: true
   *   }
   * );
   * ```
   */
  protected async executePromise<T>(fn: () => Promise<T>, options?: ExecuteOptions): Promise<T | void> {
    // Set component status to busy
    this.setStatus(StatusType.BUSY);

    // Initialize the result variable
    let result: T | void = undefined;

    try {
      // Exeucte the provided function
      result = await fn();

      // Set component status to success
      this.setStatus(StatusType.SUCCESS, options?.defaultSuccessMessage, options?.resetStatusDuration);
    } catch (error: any) {
      // Set component status to error
      this.setStatus(StatusType.ERROR, error?.message || options?.defaultErrorMessage, options?.resetStatusDuration);

      // If execute option is set to throw errors, rethrow the error.
      if (options?.throwErrors) throw error;
    }

    // Return the result of the function
    return result;
  }

  /**
   * Executes a synchronous function with automatic status handling.
   * 
   * This method provides automatic status management during function execution:
   * - Sets status to BUSY when starting
   * - Sets status to SUCCESS on successful completion
   * - Sets status to ERROR on error with the error message
   * - Optionally throws or suppresses errors
   * 
   * @param fn Synchronous function to be executed
   * @param options Configuration options for execution and status handling
   * @returns The result of the executed function or void if error occurred
   * 
   * @example
   * ```typescript
   * // Basic usage
   * const result = this.executeSync(() => this.calculateTotal(items));
   * 
   * // With options
   * const userData = this.executeSync(
   *   () => this.userMapper.transformData(rawData),
   *   {
   *     defaultSuccessMessage: 'Data transformed',
   *     defaultErrorMessage: 'Failed to transform data',
   *     resetStatusDuration: 2000,
   *     throwErrors: true
   *   }
   * );
   * ```
   */
  protected executeSync<T>(fn: () => T, options?: ExecuteOptions): T | void {
    // Set component status to busy
    this.setStatus(StatusType.BUSY);

    // Initialize the result variable
    let result: T | void = undefined;

    try {
      // Execute the provided function
      result = fn();

      // Set component status to success
      this.setStatus(StatusType.SUCCESS, options?.defaultSuccessMessage, options?.resetStatusDuration);
    } catch (error: any) {
      // Set component status to error
      this.setStatus(StatusType.ERROR, error?.message || options?.defaultErrorMessage, options?.resetStatusDuration);

      // If execute option is set to throw errors, rethrow the error.
      if (options?.throwErrors) throw error;
    }

    // Return the result of the function
    return result;
  }
  //#endregion

  //#region Status Methods
  /**
   * Sets the component status with optional message and auto-reset functionality.
   * 
   * This method allows setting the component's current state (busy, error, etc.) along
   * with an optional descriptive message. The status can automatically reset after
   * a specified duration.
   * 
   * @param type The status type to set (IDLE, BUSY, ERROR, WARNING, SUCCESS)
   * @param message Optional descriptive message to associate with the status
   * @param duration Optional time in milliseconds after which the status will
   *                 automatically reset to IDLE
   * 
   * @example
   * // Set busy status with a message
   * this.setStatus(StatusType.BUSY, 'Loading data...');
   * 
   * @example
   * // Set success status with auto-reset after 3 seconds
   * this.setStatus(StatusType.SUCCESS, 'Operation completed', 3000);
   * 
   * @example
   * // Set error status without a message
   * this.setStatus(StatusType.ERROR);
   */
  protected setStatus(type: StatusType, message?: string, duration?: number): void {
    this.status.set({ type, ...(message && { message }) });

    // If duration is provided, set a timeout to clear the message
    if (duration) this._statusResetTimeout = setTimeout(() => this.resetStatus(), duration);
  }

  /**
   * Resets the component's status to null and clears any active status timeout.
   * 
   * This method should be called when:
   * - You want to clear the current component status
   * - A process has completed and no status should be displayed
   * - You need to cancel a status that was scheduled to automatically reset
   * 
   * @example
   * ```typescript
   * // Clear an error message manually
   * if (userDismissedError) {
   *   this.resetStatus();
   * }
   * ```
   */
  protected resetStatus(): void {
    // Clear the current status by setting it to initial.
    this.status.set(this.INITIAL_STATUS);

    // Cancel any pending automatic status reset timeout to prevent memory leaks
    clearTimeout(this._statusResetTimeout);
  }
  //#endregion
}
